import { shouldAutoConfirmVeryHighConfidence } from "@/utils/tokenConfidence";

export type ContinueSection = {
  status: string;
  session_id: string | null;
  section_index?: number;
  section_identifier?: string | null;
};

export type ContinueAction =
  | {
      type: "token_matching";
      sessionId: string;
      sectionIndex: number;
      sectionIdentifier: string | null;
    }
  | { type: "shop" }
  | { type: "assignment" }
  | { type: "import" }
  | { type: "progress" }
  | { type: "completed" }
  | { type: "error"; message: string };

export function importRunLabel(message: string | undefined): string {
  if (
    !message ||
    message === "Import queued" ||
    message === "Import running" ||
    message === "Import finished"
  ) {
    return "Import läuft…";
  }
  return message;
}

export function importDialogCounts(update: {
  progress: number;
  message: string;
  pagesDone?: number;
  pagesTotal?: number;
}): { current: number; total: number; status: string } {
  const pagesTotal = update.pagesTotal ?? 0;
  if (pagesTotal > 0) {
    const done = Math.max(0, update.pagesDone ?? 0);
    return {
      current: Math.min(done, pagesTotal),
      total: pagesTotal,
      status: importRunLabel(update.message),
    };
  }
  return {
    current: Math.max(0, update.progress || 0),
    total: 100,
    status: importRunLabel(update.message),
  };
}

function importReasons(result: {
  errors?: Array<{ error?: string }>;
}): string[] {
  return [
    ...new Set(
      (result.errors || [])
        .map((item) => item.error)
        .filter((item): item is string => Boolean(item))
    ),
  ];
}

export function importAbortText(result: {
  total_sections?: number;
  skipped_sections?: number;
  errors?: Array<{ error?: string }>;
}): string {
  const reasons = importReasons(result);
  const total = result.total_sections ?? result.skipped_sections ?? 0;
  const head = `Import abgebrochen: 0 von ${total} Seiten importiert.`;
  return reasons.length ? `${head} ${reasons.slice(0, 3).join(" · ")}` : head;
}

export function importIssueText(result: {
  imported_sections?: number;
  failed_sections?: number;
  total_sections?: number;
  skipped_sections?: number;
  errors?: Array<{ error?: string }>;
}): string | null {
  if (!result.imported_sections) return importAbortText(result);
  const reasons = importReasons(result);
  const failed = result.failed_sections || 0;
  if (!reasons.length && !failed) return null;
  const failedText = failed ? `, ${failed} fehlgeschlagen` : "";
  const tail = reasons.slice(0, 3).join(" · ");
  return tail
    ? `${result.imported_sections} Seiten importiert${failedText}. ${tail}`
    : `${result.imported_sections} Seiten importiert${failedText}.`;
}

export function nextContinueAction(
  batchStatus: string,
  sections: ContinueSection[],
  hasJobId: boolean
): ContinueAction {
  void hasJobId;
  const pending = sections.find(
    (s) => s.status === "pending_resolution" && s.session_id
  );
  if (pending?.session_id) {
    return {
      type: "token_matching",
      sessionId: pending.session_id,
      sectionIndex: pending.section_index ?? 0,
      sectionIdentifier: pending.section_identifier ?? null,
    };
  }

  if (
    batchStatus === "extracting" ||
    batchStatus === "pending" ||
    batchStatus === "importing" ||
    batchStatus === "failed" ||
    batchStatus === "cancelled"
  ) {
    return { type: "progress" };
  }

  const hasExtracted = sections.some((s) => s.status === "extracted");
  if (batchStatus === "pending_bulk_shop" || hasExtracted) {
    if (
      !hasExtracted &&
      sections.some((s) => s.status === "resolved" || s.status === "incomplete")
    ) {
      return { type: "import" };
    }
    return { type: "shop" };
  }

  if (
    batchStatus === "pending_assignment" ||
    sections.some((s) => s.status === "pending_assignment")
  ) {
    return { type: "assignment" };
  }

  if (
    batchStatus === "resolved" ||
    batchStatus === "partially_failed" ||
    batchStatus === "awaiting_import" ||
    sections.some((s) => s.status === "resolved" || s.status === "incomplete")
  ) {
    return { type: "import" };
  }

  if (batchStatus === "completed") {
    return { type: "completed" };
  }

  return {
    type: "error",
    message: `Import kann nicht fortgesetzt werden (Status: ${batchStatus || "unbekannt"}).`,
  };
}

export function teacherCodeFromTokens(
  tokens: Array<{ field?: string; token?: string }> | null | undefined
): string | null {
  const hit = (tokens || []).find(
    (row) => row.field === "teacher_code" && row.token?.trim()
  );
  return hit?.token?.trim() || null;
}

export function tokenPageBatchInfo(input: {
  batchId: string;
  sectionIndex?: number | null;
  sectionIdentifier?: string | null;
  totalSections?: number | null;
  startedAt?: string | null;
  teacherCode?: string | null;
  isContinueMode?: boolean;
  resolvedSections?: number | null;
}): {
  batchId: string;
  sectionIndex: number;
  totalSections: number;
  sectionIdentifier: string | null;
  isContinueMode: boolean;
  startedAt: string;
  resolvedSections: number;
} {
  const sectionIndex = input.sectionIndex ?? 0;
  const totalSections = Math.max(1, input.totalSections || sectionIndex + 1);
  const resolved = input.resolvedSections ?? sectionIndex;
  return {
    batchId: input.batchId,
    sectionIndex,
    totalSections,
    sectionIdentifier: input.teacherCode || input.sectionIdentifier || null,
    isContinueMode: input.isContinueMode ?? true,
    startedAt: input.startedAt || new Date().toISOString(),
    resolvedSections: Math.max(0, Math.min(resolved, totalSections)),
  };
}

export function visibleShopItems<T extends { field?: string }>(
  items: T[] | null | undefined
): T[] {
  return (items || []).filter((item) => item.field !== "teacher_code");
}

export function pendingTeachersForDialog(
  importPending: string[] | null | undefined,
  pendingStatus: { pending_teachers?: string[] } | null | undefined
): string[] {
  const fromStatus = pendingStatus?.pending_teachers;
  if (fromStatus && fromStatus.length > 0) return fromStatus;
  return importPending || [];
}

export type TokenMatchLike = {
  token?: string;
  field?: string;
  confidence?: number;
  pattern_source?: string | null;
  source?: string | null;
  auto_confirmed?: boolean;
  wasPreviouslyConfirmed?: boolean;
};

export type UnmatchedTokenLike = {
  token?: string;
  meaningful?: boolean;
  irrelevant?: boolean;
  positions?: { count?: number };
};

export function hasMeaningfulUnmatchedTokens(
  unmatched: UnmatchedTokenLike[]
): boolean {
  return unmatched.some(
    (ut) => ut.meaningful === true || ut.irrelevant !== true
  );
}

export function shouldSkipTokenMatching(
  matched: TokenMatchLike[],
  unmatched: UnmatchedTokenLike[],
  unmatchedHeaderCodes: string[] = []
): boolean {
  const hasUnconfirmedMatches = matched.some(
    (mt) =>
      !mt.auto_confirmed &&
      !mt.wasPreviouslyConfirmed &&
      !shouldAutoConfirmVeryHighConfidence(mt) &&
      mt.field !== "ignore"
  );
  if (hasUnconfirmedMatches) return false;
  if (hasMeaningfulUnmatchedTokens(unmatched)) return false;
  if (unmatchedHeaderCodes.length > 0) return false;
  return true;
}

export function autoResolutionsFromMatched(matched: TokenMatchLike[]) {
  return matched
    .filter((mt) => mt.token && mt.field && mt.field !== "ignore")
    .map((mt) => ({
      token: mt.token as string,
      field: mt.field as string,
      confirmed:
        mt.wasPreviouslyConfirmed === true ||
        mt.auto_confirmed === true ||
        shouldAutoConfirmVeryHighConfidence(mt),
    }));
}
